import { csvField } from '@/features/monopoly/winnersCsv';
import type { MinionsStrings } from './i18n';
import type { MinionsPayload } from './report';
import { playsPerPlayer, timePerPlayerSec, weekPlaysPerPlayer, weekTimePerPlayerSec } from './derive';

export { downloadCsv } from '@/features/monopoly/winnersCsv';

/*
 * The weekly summary as a CSV, built in the browser from the payload — the
 * dashboard's table, a row per week, then the campaign total.
 *
 * As for the other Système U page: semicolon-separated with a UTF-8 BOM for a
 * French Excel, headers in the page's language, raw numbers (no thousands
 * separator) with the language's decimal mark, empty for a figure not measured.
 * Fields go through the shared formula-injection guard.
 */

const SEP = ';';
const BOM = '\uFEFF';

const hours = (sec: number | null) => (sec == null ? null : Math.round(sec / 3600));
const whole = (n: number | null) => (n == null ? null : Math.round(n));
const decimal = (n: number | null, t: MinionsStrings) => (n == null ? null : String(Number(n.toFixed(2))).replace('.', t.csvDecimal));

export function weeklySummaryCsv(data: MinionsPayload, t: MinionsStrings): string {
  const h = t.csvWeekly;
  const header = [
    h.week,
    h.start,
    h.end,
    h.status,
    h.uniquePlayers,
    h.newPlayers,
    h.plays,
    h.playsPerPlayer,
    h.tickets,
    h.loginMissions,
    h.invitesSent,
    h.timeHours,
    h.timePerPlayerSec,
  ];
  const rows: Array<Array<string | number | null>> = data.weeks.map((w) => [
    w.week,
    w.start,
    w.end,
    t.weekStatus[w.status],
    w.uniquePlayers,
    w.newPlayers,
    w.plays,
    decimal(weekPlaysPerPlayer(w), t),
    w.tickets,
    w.loginMissions,
    w.invitesSent,
    hours(w.timeSec),
    whole(weekTimePerPlayerSec(w)),
  ]);
  const c = data.totals;
  rows.push([
    t.csvTotal,
    data.campaign.start,
    data.campaign.end,
    '',
    c.uniquePlayers,
    // Every player is new once: the campaign's new players are its unique players.
    c.uniquePlayers,
    c.plays,
    decimal(playsPerPlayer(c), t),
    c.tickets,
    c.loginMission.completed,
    c.invites.sent,
    hours(c.totalTimeSec),
    whole(timePerPlayerSec(c)),
  ]);
  const line = (cells: Array<string | number | null>) => cells.map((v) => csvField(v == null ? '' : String(v))).join(SEP);
  return BOM + [line(header), ...rows.map(line)].join('\r\n') + '\r\n';
}
